import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Coach as CoachModel } from '../model/Coach';
import {
    Division as DivisionModel,
    Week as WeekModel,
    PlayoffStructure,
    PostseasonSeedSnapshotRow,
    MatchResult
} from '../model/Season';
import {
    buildBridgePathsToFinal,
    buildSingleEliminationBracket,
    buildWingConnectorPathsLeftToRight,
    buildWingConnectorPathsRightWing,
    computeBracketVerticalLayout,
    computeCenteredColumnBracketLayout,
    roundDisplayName,
    splitBracketForMirroredView,
    type BracketMatchView,
    type BracketSide,
    type PlayoffSeedRow
} from '../utils/playoffBracketBuild';
import {
    buildReseedEliminationRounds,
    expectedReseedPlayoffRoundCount,
    getPlayoffSeedRowsForBracket,
    getResolvedStructuralTeamIds,
    matchByIdFromRounds,
    resolveBracketByeCount,
    resolveSideForDisplay,
    shouldUseReseedByePlayoffFormat,
    type PlayoffResultsMap
} from '../utils/playoffResultsResolve';

/** Slightly wider than legacy 232px so desktop uses more horizontal space before scaling. */
const COL_WIDTH_PX = 256;
const GUTTER_PX = 44;
const MATCH_HEIGHT_PX = 92;
const BRIDGE_PX = 40;
const FINAL_COL_W = 276;
/** Width of the “floating bye” column while the opening playoff round is incomplete. */
const BYE_FLOAT_COL_W = 276;
/** Max scale-up on wide viewports (bracket narrower than container). */
const BRACKET_MAX_SCALE_UP = 1.12;
const LINE = '#1e293b';
const LINE_WIDTH = 2;

type MatchSideResultState = 'neutral' | 'winner' | 'loser';

const LAYOUT_OPTS = {
    matchHeightPx: MATCH_HEIGHT_PX,
    paddingVerticalPx: 18,
    baseGapPx: 20
} as const;

const BracketWrap = styled.div`
    margin-top: 0.5em;
    box-sizing: border-box;
`;

const BracketFitViewport = styled.div`
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
`;

const BracketScaleOuter = styled.div<{ $w: number; $h: number }>`
    width: ${p => p.$w}px;
    height: ${p => p.$h}px;
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
`;

const BracketScaleInner = styled.div<{ $scale: number; $contentW: number; $contentH: number }>`
    width: ${p => p.$contentW}px;
    height: ${p => p.$contentH}px;
    transform: scale(${p => p.$scale});
    transform-origin: top left;
`;

const BracketFrame = styled.div`
    position: relative;
`;

const Arena = styled.div`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 50%, #f8fafc 100%);
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
`;

/** Small inset inside the arena so cards clear the border (keeps compact). */
const ArenaInset = styled.div`
    padding: clamp(6px, 1.4vw, 10px) clamp(8px, 1.8vw, 14px);
    box-sizing: border-box;
`;

/** Absolutely positioned SVG + match columns (no background — Arena provides the field). */
const BracketCanvas = styled.div<{ $w: number; $h: number }>`
    position: relative;
    width: ${p => p.$w}px;
    height: ${p => p.$h}px;
`;

const ConnectorSvg = styled.svg`
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    pointer-events: none;
`;

const RoundColumn = styled.div<{ $left: number; $width: number; $height: number }>`
    position: absolute;
    left: ${p => p.$left}px;
    top: 0;
    width: ${p => p.$width}px;
    height: ${p => p.$height}px;
    z-index: 2;
`;

const ProMatchCard = styled.div<{ $isFinal?: boolean; $clickable?: boolean }>`
    position: absolute;
    left: 0;
    right: 0;
    height: ${MATCH_HEIGHT_PX}px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid ${p => (p.$isFinal ? '#64748b' : '#e2e8f0')};
    box-shadow:
        0 1px 3px rgba(15, 23, 42, 0.08),
        ${p => (p.$isFinal ? '0 0 0 2px rgba(251, 191, 36, 0.4)' : 'none')};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
`;

const SlotRow = styled.div<{ $top?: boolean; $resultState?: MatchSideResultState }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.28rem 0.45rem;
    min-height: 0;
    background: ${p => {
        if (p.$resultState === 'winner') return '#f0fdf4';
        if (p.$resultState === 'loser') return p.$top ? '#e2e8f0' : '#f1f5f9';
        return p.$top ? '#f8fafc' : '#fff';
    }};
    border-bottom: ${p => (p.$top ? '2px solid #cbd5e1' : 'none')};
    opacity: ${p => (p.$resultState === 'loser' ? 0.55 : 1)};
    filter: ${p => (p.$resultState === 'loser' ? 'grayscale(0.35)' : 'none')};
`;

const TeamLine = styled.div`
    font-weight: 600;
    font-size: 0.92rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
`;

const CoachLine = styled.div`
    font-size: 0.8rem;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 0.1rem;
`;

const SeedBadge = styled.span`
    display: inline-block;
    margin-right: 0.3em;
    font-size: 0.74rem;
    font-weight: 800;
    color: #0f172a;
    background: #e2e8f0;
    border-radius: 3px;
    padding: 0.04em 0.32em;
    vertical-align: middle;
`;

const WinnerMark = styled.span`
    margin-left: 0.35em;
    font-size: 0.8rem;
    vertical-align: middle;
`;

const ByeLine = styled.div`
    font-style: italic;
    color: #94a3b8;
    font-size: 0.84rem;
`;

const EmptyMessage = styled.div`
    padding: 1.25em;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #475569;
    font-size: 0.95rem;
`;

const ByeFloatColumn = styled.div<{ $left: number; $height: number }>`
    position: absolute;
    left: ${p => p.$left}px;
    top: 0;
    width: ${BYE_FLOAT_COL_W}px;
    height: ${p => p.$height}px;
    z-index: 2;
    padding-top: 10px;
    box-sizing: border-box;
`;

const ByeFloatTitle = styled.div`
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    margin-bottom: 0.35rem;
`;

const ByeFloatHint = styled.p`
    margin: 0 0 0.5rem;
    font-size: 0.76rem;
    line-height: 1.35;
    color: #64748b;
`;

const ByeTeamCard = styled.div`
    border-radius: 6px;
    background: #fff;
    border: 1px dashed #94a3b8;
    padding: 0.35rem 0.45rem;
    margin-bottom: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
`;

const ResultChip = styled.div`
    font-size: 0.76rem;
    font-weight: 600;
    color: #0f766e;
    padding: 0.15rem 0.45rem;
    margin: 0.15rem 0.45rem 0;
    background: #ecfdf5;
    border-radius: 4px;
    align-self: flex-start;
`;

const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
`;

const ModalPanel = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    max-width: 420px;
    width: 100%;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.2);
`;

const ModalActions = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    justify-content: flex-end;
`;

interface PostSeasonBracketProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
        playoffStructure?: PlayoffStructure;
        postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
        playoffResults?: PlayoffResultsMap;
    };
    leagueCoaches: CoachModel[];
    onPlayoffResultsUpdate?: (matchId: string, results: MatchResult) => void | Promise<void>;
}

function validPlayoffStructure(ps: PlayoffStructure | undefined): ps is PlayoffStructure {
    return !!(
        ps &&
        typeof ps.divisionWinnersPerDivision === 'number' &&
        typeof ps.wildcardCount === 'number' &&
        typeof ps.byeCount === 'number'
    );
}

function renderSide(side: BracketSide, resultState: MatchSideResultState = 'neutral'): React.ReactNode {
    if (side.kind === 'bye') {
        return <ByeLine>Bye</ByeLine>;
    }
    if (side.kind === 'winnerOf') {
        return null;
    }
    return (
        <div>
            <TeamLine>
                <SeedBadge title="Seed">{side.seed}</SeedBadge>
                {side.teamName}
                {resultState === 'winner' && (
                    <WinnerMark title="Winner" aria-label="Winner">
                        ✅
                    </WinnerMark>
                )}
            </TeamLine>
            <CoachLine>{side.coachName}</CoachLine>
        </div>
    );
}

function zoneWidthPx(numCols: number): number {
    return numCols * COL_WIDTH_PX + Math.max(0, numCols - 1) * GUTTER_PX;
}

function bracketTotalWidthPx(numRounds: number): number {
    return zoneWidthPx(numRounds);
}

function buildLinearConnectorPaths(layout: { tops: number[][]; connectors: { roundFrom: number; feederA: number; feederB: number; parentIndex: number }[] }): string[] {
    const { tops, connectors } = layout;
    const paths: string[] = [];
    const cy = (r: number, mi: number) => tops[r][mi] + MATCH_HEIGHT_PX / 2;
    const xRight = (r: number) => r * (COL_WIDTH_PX + GUTTER_PX) + COL_WIDTH_PX;
    const midX = (r: number) => xRight(r) + GUTTER_PX / 2;
    const xNext = (r: number) => (r + 1) * (COL_WIDTH_PX + GUTTER_PX);

    for (const c of connectors) {
        const { roundFrom, feederA, feederB, parentIndex } = c;
        const yA = cy(roundFrom, feederA);
        const yB = cy(roundFrom, feederB);
        const yP = cy(roundFrom + 1, parentIndex);
        const xr = xRight(roundFrom);
        const xm = midX(roundFrom);
        const xn = xNext(roundFrom);
        paths.push(`M ${xr} ${yA} L ${xm} ${yA}`);
        paths.push(`M ${xr} ${yB} L ${xm} ${yB}`);
        paths.push(`M ${xm} ${Math.min(yA, yB)} L ${xm} ${Math.max(yA, yB)}`);
        paths.push(`M ${xm} ${yP} L ${xn} ${yP}`);
    }
    return paths;
}

function getMatchWinnerTeamId(
    match: BracketMatchView,
    matchById: Map<string, BracketMatchView>,
    playoffResults: PlayoffResultsMap | undefined
): string | null {
    const result = playoffResults?.[match.id];
    if (!result || result.homeTeamScore === result.awayTeamScore) return null;
    const structural = getResolvedStructuralTeamIds(match, matchById, playoffResults);
    if (!structural) return null;
    return result.homeTeamScore > result.awayTeamScore ? structural.homeTeamId : structural.awayTeamId;
}

function getSideResultState(side: BracketSide, winnerTeamId: string | null, hasResult: boolean): MatchSideResultState {
    if (!hasResult || !winnerTeamId || side.kind !== 'team') return 'neutral';
    return side.teamId === winnerTeamId ? 'winner' : 'loser';
}

function useBracketFitScale(contentWidth: number, contentHeight: number): { ref: React.RefObject<HTMLDivElement>; scale: number } {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || contentWidth <= 0) {
            setScale(1);
            return;
        }

        const measure = () => {
            const w = el.clientWidth;
            if (w <= 0) {
                setScale(1);
                return;
            }
            const fit = w / contentWidth;
            if (fit < 1) {
                setScale(fit);
            } else {
                setScale(Math.min(fit, BRACKET_MAX_SCALE_UP));
            }
        };

        measure();
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [contentWidth, contentHeight]);

    return { ref, scale };
}

function BracketScaledFrame(props: {
    contentWidth: number;
    contentHeight: number;
    children: React.ReactNode;
}): React.ReactElement {
    const { ref, scale } = useBracketFitScale(props.contentWidth, props.contentHeight);
    const w = Math.ceil(props.contentWidth * scale);
    const h = Math.ceil(props.contentHeight * scale);

    return (
        <BracketFitViewport ref={ref}>
            <BracketScaleOuter $w={w} $h={h}>
                <BracketScaleInner $scale={scale} $contentW={props.contentWidth} $contentH={props.contentHeight}>
                    {props.children}
                </BracketScaleInner>
            </BracketScaleOuter>
        </BracketFitViewport>
    );
}

export const PostSeasonBracket: React.FC<PostSeasonBracketProps> = ({ seasonData, leagueCoaches, onPlayoffResultsUpdate }) => {
    const playoffResults = seasonData.playoffResults;

    const seeds = useMemo(() => {
        if (!validPlayoffStructure(seasonData.playoffStructure)) {
            return [] as PlayoffSeedRow[];
        }
        return getPlayoffSeedRowsForBracket(seasonData, leagueCoaches);
    }, [
        seasonData.divisions,
        seasonData.weeks,
        seasonData.playoffStructure,
        seasonData.postseasonSeedsSnapshot,
        leagueCoaches
    ]);

    /** Uses implicit padding byes when `byeCount` is 0 or inconsistent (e.g. 6 teams, 3 byes → treat as 2). */
    const byeCount = useMemo(
        () => resolveBracketByeCount(seeds, seasonData.playoffStructure),
        [seeds, seasonData.playoffStructure]
    );

    const useReseedByeLayout = useMemo(() => shouldUseReseedByePlayoffFormat(seeds, byeCount), [seeds, byeCount]);

    const rounds = useMemo(() => {
        if (!seeds.length) return [] as BracketMatchView[][];
        if (useReseedByeLayout) {
            return buildReseedEliminationRounds(seeds, byeCount, playoffResults);
        }
        return buildSingleEliminationBracket(seeds);
    }, [seeds, byeCount, playoffResults, useReseedByeLayout]);

    const matchById = useMemo(() => matchByIdFromRounds(rounds), [rounds]);

    const roundLabelDepth = useMemo(() => {
        if (shouldUseReseedByePlayoffFormat(seeds, byeCount)) {
            return expectedReseedPlayoffRoundCount(seeds, byeCount);
        }
        return Math.max(1, rounds.length);
    }, [seeds, byeCount, rounds.length]);

    const seedByTeamId = useMemo(() => new Map(seeds.map(s => [s.teamId, s] as const)), [seeds]);

    const [modalMatch, setModalMatch] = useState<BracketMatchView | null>(null);
    const [scoreForm, setScoreForm] = useState({
        homeTeamScore: 0,
        awayTeamScore: 0,
        homeTeamCasInflicted: 0,
        awayTeamCasInflicted: 0,
        overtime: false
    });

    const openPlayoffResultModal = (m: BracketMatchView) => {
        if (!onPlayoffResultsUpdate) return;
        const structural = getResolvedStructuralTeamIds(m, matchById, playoffResults);
        if (!structural) return;
        const existing = playoffResults?.[m.id];
        setScoreForm({
            homeTeamScore: existing?.homeTeamScore ?? 0,
            awayTeamScore: existing?.awayTeamScore ?? 0,
            homeTeamCasInflicted: existing?.homeTeamCasInflicted ?? 0,
            awayTeamCasInflicted: existing?.awayTeamCasInflicted ?? 0,
            overtime: existing?.overtime ?? false
        });
        setModalMatch(m);
    };

    const savePlayoffModal = async () => {
        if (!modalMatch || !onPlayoffResultsUpdate) return;
        const structural = getResolvedStructuralTeamIds(modalMatch, matchById, playoffResults);
        if (!structural) return;
        const results: MatchResult = {
            overtime: scoreForm.overtime,
            homeTeamScore: scoreForm.homeTeamScore,
            awayTeamScore: scoreForm.awayTeamScore,
            homeTeamCasInflicted: scoreForm.homeTeamCasInflicted,
            awayTeamCasInflicted: scoreForm.awayTeamCasInflicted,
            source: 'manual'
        };
        await onPlayoffResultsUpdate(modalMatch.id, results);
        setModalMatch(null);
    };

    const renderMatchCard = (
        m: BracketMatchView,
        options: {
            top: number;
            isFinal?: boolean;
            ariaLabel: string;
            width?: number;
        }
    ) => {
        const clickable = Boolean(onPlayoffResultsUpdate && getResolvedStructuralTeamIds(m, matchById, playoffResults));
        const pr = playoffResults?.[m.id];
        const winnerTeamId = getMatchWinnerTeamId(m, matchById, playoffResults);
        const home = resolveSideForDisplay(m.home, matchById, playoffResults, seedByTeamId);
        const away = resolveSideForDisplay(m.away, matchById, playoffResults, seedByTeamId);
        const homeState = getSideResultState(home, winnerTeamId, Boolean(pr));
        const awayState = getSideResultState(away, winnerTeamId, Boolean(pr));

        return (
            <ProMatchCard
                key={m.id}
                style={{ top: options.top, ...(options.width ? { width: options.width } : {}) }}
                $isFinal={options.isFinal}
                aria-label={options.ariaLabel}
                $clickable={clickable}
                onClick={clickable ? () => openPlayoffResultModal(m) : undefined}
            >
                {pr && (
                    <ResultChip>
                        {pr.homeTeamScore}-{pr.awayTeamScore}
                        {pr.overtime ? ' OT' : ''}
                    </ResultChip>
                )}
                <SlotRow $top $resultState={homeState}>
                    {renderSide(home, homeState)}
                </SlotRow>
                <SlotRow $resultState={awayState}>{renderSide(away, awayState)}</SlotRow>
            </ProMatchCard>
        );
    };

    const split = useMemo(() => {
        if (!rounds.length || useReseedByeLayout) return null;
        return splitBracketForMirroredView(rounds);
    }, [rounds, useReseedByeLayout]);

    const reseedLayout = useMemo(() => {
        if (!useReseedByeLayout || rounds.length === 0) return null;
        return computeCenteredColumnBracketLayout(rounds, {
            matchHeightPx: MATCH_HEIGHT_PX,
            gapPx: LAYOUT_OPTS.baseGapPx,
            paddingVerticalPx: LAYOUT_OPTS.paddingVerticalPx,
            connectorMinRoundFrom: rounds.length >= 2 ? 1 : 99
        });
    }, [useReseedByeLayout, rounds]);

    const reseedLinearPaths = useMemo(() => {
        if (!reseedLayout) return [];
        return buildLinearConnectorPaths(reseedLayout);
    }, [reseedLayout]);

    const reseedMirror = useMemo(() => {
        if (!useReseedByeLayout || rounds.length < 2 || rounds[1].length !== 2) return null;
        const semifinalRound = 1;
        const leftSemi = rounds[semifinalRound][0];
        const rightSemi = rounds[semifinalRound][1];
        const finalMatch =
            rounds[2]?.[0] ??
            ({
                id: 'r2-m0',
                roundIndex: 2,
                matchIndex: 0,
                home: { kind: 'winnerOf', matchId: leftSemi.id, label: 'Semifinal 1' },
                away: { kind: 'winnerOf', matchId: rightSemi.id, label: 'Semifinal 2' }
            } satisfies BracketMatchView);

        const arenaH = MATCH_HEIGHT_PX + LAYOUT_OPTS.paddingVerticalPx * 2;
        const matchTop = (arenaH - MATCH_HEIGHT_PX) / 2;
        const finalLeftX = COL_WIDTH_PX + BRIDGE_PX;
        const finalRightX = finalLeftX + FINAL_COL_W;
        const rightLeftX = finalRightX + BRIDGE_PX;
        const finalistY = matchTop + MATCH_HEIGHT_PX / 2;
        const paths = buildBridgePathsToFinal({
            leftInnerRightX: COL_WIDTH_PX,
            leftFinalistY: finalistY,
            finalLeftX,
            finalRightX,
            rightFinalistY: finalistY,
            rightInnerLeftX: rightLeftX
        });
        const totalW = COL_WIDTH_PX + BRIDGE_PX * 2 + FINAL_COL_W + COL_WIDTH_PX;

        return {
            arenaH,
            finalLeftX,
            matchTop,
            paths,
            rightLeftX,
            totalW,
            leftSemi,
            rightSemi,
            finalMatch
        };
    }, [rounds, useReseedByeLayout]);

    const linearLayout = useMemo(
        () =>
            rounds.length && split && !split.isMirrored && !useReseedByeLayout
                ? computeBracketVerticalLayout(rounds, LAYOUT_OPTS)
                : null,
        [rounds, split, useReseedByeLayout]
    );

    const leftLayout = useMemo(
        () =>
            split?.isMirrored && split.leftRounds.length ? computeBracketVerticalLayout(split.leftRounds, LAYOUT_OPTS) : null,
        [split]
    );

    const rightLayout = useMemo(
        () =>
            split?.isMirrored && split.rightRounds.length
                ? computeBracketVerticalLayout(split.rightRounds, LAYOUT_OPTS)
                : null,
        [split]
    );

    const mirroredSvgPaths = useMemo(() => {
        if (!split?.isMirrored || !leftLayout || !rightLayout) return null;
        const nL = split.leftRounds.length;
        const nR = split.rightRounds.length;
        const leftW = zoneWidthPx(nL);
        const innerRightX = (nL - 1) * (COL_WIDTH_PX + GUTTER_PX) + COL_WIDTH_PX;
        const finalLeftX = innerRightX + BRIDGE_PX;
        const finalRightX = finalLeftX + FINAL_COL_W;
        const rightInnerLeftX = finalRightX + BRIDGE_PX;

        const arenaH = Math.max(
            leftLayout.totalHeightPx,
            rightLayout.totalHeightPx,
            MATCH_HEIGHT_PX + 36
        );
        const leftPad = (arenaH - leftLayout.totalHeightPx) / 2;
        const rightPad = (arenaH - rightLayout.totalHeightPx) / 2;

        const leftPaths = buildWingConnectorPathsLeftToRight(leftLayout, 0, COL_WIDTH_PX, GUTTER_PX, MATCH_HEIGHT_PX);
        const colLeftRight = (r: number) => rightInnerLeftX + (nR - 1 - r) * (COL_WIDTH_PX + GUTTER_PX);
        const rightPaths = buildWingConnectorPathsRightWing(rightLayout, colLeftRight, COL_WIDTH_PX, MATCH_HEIGHT_PX);

        const lr = nL - 1;
        const rr = nR - 1;
        const yL = leftPad + leftLayout.tops[lr][0] + MATCH_HEIGHT_PX / 2;
        const yR = rightPad + rightLayout.tops[rr][0] + MATCH_HEIGHT_PX / 2;
        const bridges = buildBridgePathsToFinal({
            leftInnerRightX: innerRightX,
            leftFinalistY: yL,
            finalLeftX,
            finalRightX,
            rightFinalistY: yR,
            rightInnerLeftX
        });

        const all: string[] = [];
        for (const d of leftPaths) {
            all.push(applyTranslateYToPathD(d, leftPad));
        }
        for (const d of rightPaths) {
            all.push(applyTranslateYToPathD(d, rightPad));
        }
        all.push(...bridges);
        const champMidWidth = BRIDGE_PX * 2 + FINAL_COL_W;
        const totalW = leftW + champMidWidth + zoneWidthPx(nR);
        return { paths: all, arenaH, leftPad, rightPad, leftW, finalLeftX, rightInnerLeftX, nL, nR, totalW };
    }, [split, leftLayout, rightLayout]);

    const linearPaths = useMemo(() => {
        if (!linearLayout) return [];
        return buildLinearConnectorPaths(linearLayout);
    }, [linearLayout]);

    const playoffModal =
        modalMatch &&
        onPlayoffResultsUpdate && (
            <ModalBackdrop onClick={() => setModalMatch(null)}>
                <ModalPanel onClick={e => e.stopPropagation()} role="dialog" aria-modal aria-labelledby="playoff-result-title">
                    <h4 id="playoff-result-title" style={{ margin: '0 0 0.75rem' }}>
                        Playoff game result
                    </h4>
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
                        Match <code>{modalMatch.id}</code> — scores are for the bracket home slot (top) vs away slot (bottom).
                    </p>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem' }}>Home score</label>
                    <input
                        type="number"
                        value={scoreForm.homeTeamScore}
                        onChange={e => setScoreForm(f => ({ ...f, homeTeamScore: Number(e.target.value) }))}
                        style={{ width: '100%', marginBottom: '0.5rem', padding: '0.35rem' }}
                    />
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem' }}>Away score</label>
                    <input
                        type="number"
                        value={scoreForm.awayTeamScore}
                        onChange={e => setScoreForm(f => ({ ...f, awayTeamScore: Number(e.target.value) }))}
                        style={{ width: '100%', marginBottom: '0.5rem', padding: '0.35rem' }}
                    />
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem' }}>Home CAS</label>
                    <input
                        type="number"
                        value={scoreForm.homeTeamCasInflicted}
                        onChange={e => setScoreForm(f => ({ ...f, homeTeamCasInflicted: Number(e.target.value) }))}
                        style={{ width: '100%', marginBottom: '0.5rem', padding: '0.35rem' }}
                    />
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem' }}>Away CAS</label>
                    <input
                        type="number"
                        value={scoreForm.awayTeamCasInflicted}
                        onChange={e => setScoreForm(f => ({ ...f, awayTeamCasInflicted: Number(e.target.value) }))}
                        style={{ width: '100%', marginBottom: '0.5rem', padding: '0.35rem' }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                        <input
                            type="checkbox"
                            checked={scoreForm.overtime}
                            onChange={e => setScoreForm(f => ({ ...f, overtime: e.target.checked }))}
                        />
                        Overtime
                    </label>
                    <ModalActions>
                        <button type="button" className="btn" style={{ padding: '0.4rem 0.8rem' }} onClick={() => setModalMatch(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            style={{ padding: '0.4rem 0.8rem', background: '#0f766e', color: '#fff', border: 'none', borderRadius: 4 }}
                            onClick={() => void savePlayoffModal()}
                        >
                            Save
                        </button>
                    </ModalActions>
                </ModalPanel>
            </ModalBackdrop>
        );

    if (!validPlayoffStructure(seasonData.playoffStructure)) {
        return (
            <BracketWrap>
                <EmptyMessage>No playoff structure is configured for this season.</EmptyMessage>
            </BracketWrap>
        );
    }

    if (seeds.length === 0 || rounds.length === 0) {
        return (
            <BracketWrap>
                <EmptyMessage>Not enough teams to build a playoff bracket.</EmptyMessage>
            </BracketWrap>
        );
    }

    if (useReseedByeLayout && reseedMirror) {
        const { arenaH, finalLeftX, finalMatch, leftSemi, matchTop, paths, rightLeftX, rightSemi, totalW } = reseedMirror;

        return (
            <>
                <BracketWrap data-testid="post-season-bracket">
                    <Arena>
                        <ArenaInset>
                            <BracketScaledFrame contentWidth={totalW} contentHeight={arenaH}>
                                <BracketFrame style={{ width: totalW }}>
                                    <BracketCanvas $w={totalW} $h={arenaH}>
                                        <ConnectorSvg width={totalW} height={arenaH} aria-hidden>
                                            {paths.map((d, i) => (
                                                <path
                                                    key={i}
                                                    d={d}
                                                    stroke={LINE}
                                                    strokeWidth={LINE_WIDTH}
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            ))}
                                        </ConnectorSvg>
                                        <RoundColumn $left={0} $width={COL_WIDTH_PX} $height={arenaH}>
                                            {renderMatchCard(leftSemi, {
                                                top: matchTop,
                                                ariaLabel: `${roundDisplayName(1, roundLabelDepth)} game 1`
                                            })}
                                        </RoundColumn>
                                        <RoundColumn $left={finalLeftX} $width={FINAL_COL_W} $height={arenaH}>
                                            {renderMatchCard(finalMatch, {
                                                top: matchTop,
                                                isFinal: true,
                                                ariaLabel: 'Championship game',
                                                width: FINAL_COL_W
                                            })}
                                        </RoundColumn>
                                        <RoundColumn $left={rightLeftX} $width={COL_WIDTH_PX} $height={arenaH}>
                                            {renderMatchCard(rightSemi, {
                                                top: matchTop,
                                                ariaLabel: `${roundDisplayName(1, roundLabelDepth)} game 2`
                                            })}
                                        </RoundColumn>
                                    </BracketCanvas>
                                </BracketFrame>
                            </BracketScaledFrame>
                        </ArenaInset>
                    </Arena>
                </BracketWrap>
                {playoffModal}
            </>
        );
    }

    if (useReseedByeLayout && reseedLayout) {
        const needsByeFloat = rounds.length === 1 && byeCount > 0;
        const byeRows = [...seeds].sort((a, b) => a.seed - b.seed).slice(0, byeCount);
        const totalW = needsByeFloat ? COL_WIDTH_PX + GUTTER_PX + BYE_FLOAT_COL_W : bracketTotalWidthPx(rounds.length);
        const arenaH = reseedLayout.totalHeightPx;
        const byeColumnLeft = COL_WIDTH_PX + GUTTER_PX;

        return (
            <>
                <BracketWrap data-testid="post-season-bracket">
                    <Arena>
                        <ArenaInset>
                            <BracketScaledFrame contentWidth={totalW} contentHeight={arenaH}>
                                <BracketFrame style={{ width: totalW }}>
                                    <BracketCanvas $w={totalW} $h={arenaH}>
                                        <ConnectorSvg width={totalW} height={arenaH} aria-hidden>
                                            {reseedLinearPaths.map((d, i) => (
                                                <path
                                                    key={i}
                                                    d={d}
                                                    stroke={LINE}
                                                    strokeWidth={LINE_WIDTH}
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            ))}
                                        </ConnectorSvg>
                                        {rounds.map((matches, ri) => (
                                            <RoundColumn
                                                key={`reseed-col-${ri}`}
                                                $left={ri * (COL_WIDTH_PX + GUTTER_PX)}
                                                $width={COL_WIDTH_PX}
                                                $height={arenaH}
                                            >
                                                {matches.map((m, mi) => {
                                                    return renderMatchCard(m, {
                                                        top: reseedLayout.tops[ri][mi],
                                                        isFinal: roundLabelDepth > 0 && ri === rounds.length - 1,
                                                        ariaLabel: `${roundDisplayName(ri, roundLabelDepth)} game ${mi + 1}`
                                                    });
                                                })}
                                            </RoundColumn>
                                        ))}
                                        {needsByeFloat && (
                                            <ByeFloatColumn $left={byeColumnLeft} $height={arenaH}>
                                                <ByeFloatTitle>First-round byes</ByeFloatTitle>
                                                <ByeFloatHint>
                                                    Semifinals appear after each opening-round game has a winner. Pairings use
                                                    frozen seeds: each round, the best remaining seed plays the worst remaining
                                                    seed.
                                                </ByeFloatHint>
                                                {byeRows.map(row => (
                                                    <ByeTeamCard key={row.teamId}>
                                                        <TeamLine>
                                                            <SeedBadge title="Seed">{row.seed}</SeedBadge>
                                                            {row.teamName}
                                                        </TeamLine>
                                                        <CoachLine>{row.coachName}</CoachLine>
                                                    </ByeTeamCard>
                                                ))}
                                            </ByeFloatColumn>
                                        )}
                                    </BracketCanvas>
                                </BracketFrame>
                            </BracketScaledFrame>
                        </ArenaInset>
                    </Arena>
                </BracketWrap>
                {playoffModal}
            </>
        );
    }

    if (!split) {
        return (
            <BracketWrap>
                <EmptyMessage>Not enough teams to build a playoff bracket.</EmptyMessage>
            </BracketWrap>
        );
    }

    const totalRounds = rounds.length;

    if (split.isMirrored && leftLayout && rightLayout && mirroredSvgPaths) {
        const { paths, arenaH, leftPad, rightPad, finalLeftX, rightInnerLeftX, nR, totalW } =
            mirroredSvgPaths;

        return (
            <>
                <BracketWrap data-testid="post-season-bracket">
                    <Arena>
                        <ArenaInset>
                            <BracketScaledFrame contentWidth={totalW} contentHeight={arenaH}>
                                <BracketFrame style={{ width: totalW }}>
                                    <BracketCanvas $w={totalW} $h={arenaH}>
                                        <ConnectorSvg width={totalW} height={arenaH} aria-hidden>
                                            {paths.map((d, i) => (
                                                <path
                                                    key={i}
                                                    d={d}
                                                    stroke={LINE}
                                                    strokeWidth={LINE_WIDTH}
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            ))}
                                        </ConnectorSvg>
                                        {split.leftRounds.map((matches, ri) => (
                                            <RoundColumn
                                                key={`lc-${ri}`}
                                                $left={ri * (COL_WIDTH_PX + GUTTER_PX)}
                                                $width={COL_WIDTH_PX}
                                                $height={arenaH}
                                                style={{ paddingTop: leftPad }}
                                            >
                                                {matches.map((m, mi) => {
                                                    return renderMatchCard(m, {
                                                        top: leftLayout.tops[ri][mi],
                                                        ariaLabel: `${roundDisplayName(ri, totalRounds)} game ${mi + 1}`
                                                    });
                                                })}
                                            </RoundColumn>
                                        ))}
                                        <RoundColumn
                                            key="final-col"
                                            $left={finalLeftX}
                                            $width={FINAL_COL_W}
                                            $height={arenaH}
                                            style={{ paddingTop: (arenaH - MATCH_HEIGHT_PX) / 2 }}
                                        >
                                            {(() => {
                                                const fm = split.finalMatch;
                                                return renderMatchCard(fm, {
                                                    top: 0,
                                                    isFinal: true,
                                                    ariaLabel: 'Championship game'
                                                });
                                            })()}
                                        </RoundColumn>
                                        {split.rightRounds.map((matches, wingR) => {
                                            const cFromInner = nR - 1 - wingR;
                                            const left = rightInnerLeftX + cFromInner * (COL_WIDTH_PX + GUTTER_PX);
                                            return (
                                                <RoundColumn
                                                    key={`rc-${wingR}`}
                                                    $left={left}
                                                    $width={COL_WIDTH_PX}
                                                    $height={arenaH}
                                                    style={{ paddingTop: rightPad }}
                                                >
                                                    {matches.map((m, mi) => {
                                                        return renderMatchCard(m, {
                                                            top: rightLayout.tops[wingR][mi],
                                                            ariaLabel: `${roundDisplayName(wingR, totalRounds)} game ${mi + 1}`
                                                        });
                                                    })}
                                                </RoundColumn>
                                            );
                                        })}
                                    </BracketCanvas>
                                </BracketFrame>
                            </BracketScaledFrame>
                        </ArenaInset>
                    </Arena>
                </BracketWrap>
                {playoffModal}
            </>
        );
    }

    if (!linearLayout) {
        return (
            <BracketWrap>
                <EmptyMessage>Not enough teams to build a playoff bracket.</EmptyMessage>
            </BracketWrap>
        );
    }

    const totalW = bracketTotalWidthPx(totalRounds);
    const arenaH = linearLayout.totalHeightPx;

    return (
        <>
            <BracketWrap data-testid="post-season-bracket">
                <Arena>
                    <ArenaInset>
                        <BracketScaledFrame contentWidth={totalW} contentHeight={arenaH}>
                            <BracketFrame style={{ width: totalW }}>
                                <BracketCanvas $w={totalW} $h={arenaH}>
                                    <ConnectorSvg width={totalW} height={arenaH} aria-hidden>
                                        {linearPaths.map((d, i) => (
                                            <path
                                                key={i}
                                                d={d}
                                                stroke={LINE}
                                                strokeWidth={LINE_WIDTH}
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        ))}
                                    </ConnectorSvg>
                                    {rounds.map((matches, ri) => (
                                        <RoundColumn
                                            key={`col-${ri}`}
                                            $left={ri * (COL_WIDTH_PX + GUTTER_PX)}
                                            $width={COL_WIDTH_PX}
                                            $height={arenaH}
                                        >
                                            {matches.map((m, mi) => {
                                                return renderMatchCard(m, {
                                                    top: linearLayout.tops[ri][mi],
                                                    isFinal: totalRounds > 0 && ri === totalRounds - 1,
                                                    ariaLabel: `${roundDisplayName(ri, totalRounds)} game ${mi + 1}`
                                                });
                                            })}
                                        </RoundColumn>
                                    ))}
                                </BracketCanvas>
                            </BracketFrame>
                        </BracketScaledFrame>
                    </ArenaInset>
                </Arena>
            </BracketWrap>
            {playoffModal}
        </>
    );
};

/** Shift all Y coordinates in an SVG path `d` by `dy` (simple numeric parser for M/L segments we emit). */
function applyTranslateYToPathD(d: string, dy: number): string {
    if (dy === 0) return d;
    return d.replace(/([ML])\s+([\d.-]+)\s+([\d.-]+)/g, (_, cmd: string, x: string, y: string) => {
        const yn = parseFloat(y) + dy;
        return `${cmd} ${x} ${yn}`;
    });
}
